export type AnalysisFailurePhase = "engine_init" | "analysis" | "upload";

export interface NormalizedAnalysisError {
  phase: AnalysisFailurePhase;
  userMessage: string;
  diagnostic: string;
}

export interface AnalysisFailureReport {
  attempts: number;
  error: string;
  phase: AnalysisFailurePhase;
  preset: string;
  capabilities: {
    crossOriginIsolated: boolean;
    sharedArrayBuffer: boolean;
    hardwareConcurrency: number | null;
  };
}

const USER_MESSAGES: Record<AnalysisFailurePhase, string> = {
  engine_init: "The analysis engine could not start. Refresh the page or choose Quick analysis and try again.",
  analysis: "Analysis stopped before it finished. Retry this game; if it repeats, choose Quick analysis.",
  upload: "Analysis finished but could not be saved. Check your connection, then retry this game.",
};

const useful = (value: unknown): value is string => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed !== "" && !/^(?:undefined|null)$/i.test(trimmed);
};

const redact = (value: string): string =>
  value
    .replace(/\bBearer\s+\S+/gi, "Bearer [redacted]")
    .replace(/\b(api[_-]?key|token|secret|password)=[^\s&]+/gi, "$1=[redacted]")
    .replace(/(https?:\/\/[^\s?]+)\?\S+/gi, "$1?[redacted]")
    .replace(/\b(?:undefined|null)\b/gi, "[missing value]")
    .slice(0, 500);

export const isAnalysisCancellation = (error: unknown): boolean =>
  !!error && typeof error === "object" && "name" in error && error.name === "AbortError";

export const analysisCapabilities = (): AnalysisFailureReport["capabilities"] => ({
  crossOriginIsolated: globalThis.crossOriginIsolated === true,
  sharedArrayBuffer: typeof globalThis.SharedArrayBuffer !== "undefined",
  hardwareConcurrency:
    typeof navigator !== "undefined" &&
    Number.isInteger(navigator.hardwareConcurrency) &&
    navigator.hardwareConcurrency > 0
      ? navigator.hardwareConcurrency
      : null,
});

export const normalizeAnalysisError = (error: unknown, phase: AnalysisFailurePhase): NormalizedAnalysisError => {
  const record = error && typeof error === "object" ? (error as Record<string, unknown>) : null;
  const name = useful(record?.name) ? record.name : typeof error === "string" ? "ThrownString" : "UnknownFailure";
  const message = useful(record?.message) ? record.message : useful(error) ? error : "No diagnostic detail supplied";
  const status = typeof record?.status === "number" ? ` status=${record.status}` : "";

  return {
    phase,
    userMessage: USER_MESSAGES[phase],
    diagnostic: redact(`${phase} ${name}${status}: ${message}`),
  };
};
